import {useEffect, useRef, useState} from "react";
import {setupMobileDetection, isAndroid} from "./utils/utils";

import {
    createVisibilityHandler,
    createBrowserSpeechRecognition,
    createWebSocketService,
    createAudioProcessor
} from "./services";

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const useBrowserSpeechRecognition = !!SpeechRecognition;

const createGlobalService = (callback) => {
    if (useBrowserSpeechRecognition && !isAndroid()) {
        return createBrowserSpeechRecognition(callback);
    } else {
        return createWebSocketService(callback);
    }
};

const SpeechRecognitionEngine = ({onTranscriptUpdate, isItModal, loading, initLoad = false, isModalOpen = false}) => {
    const [isMobile, setIsMobile] = useState(false);
    const isRecordingRef = useRef(false);
    const serviceRef = useRef(null);
    const audioProcessorRef = useRef(null);
    const isBrowserMode = !!SpeechRecognition;
    const isWebSocketMode = !isBrowserMode || isAndroid();
    const recognitionService = useRef(createGlobalService((data) => {
        if (typeof data === 'string') {
            onTranscriptUpdate?.(data);
        } else if (data && typeof data === 'object') {
            onTranscriptUpdate?.(data);
        }
    })).current;
    const visibilityHandler = useRef(createVisibilityHandler(startRecognition, stopRecognition)).current;

    useEffect(() => {

        if (!serviceRef.current && isWebSocketMode) {
            serviceRef.current =
                recognitionService.setupWebSocket(
                    visibilityHandler.isActiveTabRef,
                    isRecordingRef
                );
        }

        if (isWebSocketMode && !audioProcessorRef.current) {
            audioProcessorRef.current = createAudioProcessor(recognitionService);
        }
        if (initLoad) {
            startRecognition();
        }

        const cleanupVisibility = visibilityHandler.setupVisibilityHandling();

        return () => {
            cleanupVisibility();
            stopRecognition(true);
        };
    }, []);

    useEffect(() => {
        recognitionService.updateCallback(onTranscriptUpdate);
    }, [onTranscriptUpdate]);

    useEffect(() => {
        if (recognitionService.setModalState) {
            recognitionService.setModalState(isModalOpen);
        }
    }, [isModalOpen, isWebSocketMode]);

    useEffect(() => {
        return setupMobileDetection(setIsMobile);
    }, []);

    async function startRecognition() {
        if (isRecordingRef.current) {
            return;
        }

        try {
            const connected = recognitionService.isConnected();
            if (!connected && isWebSocketMode) {
                serviceRef.current =
                    recognitionService.setupWebSocket(
                        visibilityHandler.isActiveTabRef,
                        isRecordingRef
                    );

                if (audioProcessorRef.current) {
                    audioProcessorRef.current.stopAudioProcessing(true);
                }

                audioProcessorRef.current =
                    createAudioProcessor(recognitionService, false);
            }

            let success;
            if (!isWebSocketMode && !isAndroid()) {
                success = recognitionService.startRecognition();
            } else {
                success = await audioProcessorRef.current.startAudioProcessing();
            }

            if (success) {
                isRecordingRef.current = true;
                visibilityHandler.setHasStartedRecognition(true);
            } else {
                console.warn("[Engine] Failed to start recognition");
            }
        } catch (error) {
            console.error("[Engine] Error in startRecognition:", error);
        }
    }

    function stopRecognition(intentional = false, fromVisibilityChange = false) {

        if (!isRecordingRef.current && !serviceRef.current) {
            console.warn("[Engine] Nothing to stop");
            return;
        }

        if (!isWebSocketMode) {
            recognitionService.stopRecognition();
        } else {
            audioProcessorRef.current?.stopAudioProcessing(intentional);
        }

        if (intentional) {
            if (!isWebSocketMode) {
                recognitionService.closeRecognition();
            } else {
                recognitionService.closeWebSocket();
            }
            serviceRef.current = null;
        }

        isRecordingRef.current = false;

        if (intentional && !fromVisibilityChange) {
            visibilityHandler.setHasStartedRecognition(false);
        }
    }

    return null;
};

export default SpeechRecognitionEngine;
